import React, { useState } from 'react';
import {
  LifeBuoy,
  Send,
  CheckCircle2,
  AlertCircle,
  Clock,
  MessageSquare,
  FileText,
  Loader2,
  ChevronRight,
  Headphones,
  Zap,
  Shield,
} from 'lucide-react';
import api from '../api/api';
import styles from './SupportPage.module.css';

// ─────────────────────────────────────────────────────────────
// DONNÉES STATIQUES
// ─────────────────────────────────────────────────────────────
const QUICK_TOPICS = [
  { label: 'Problème de connexion', icon: Shield },
  { label: 'Bug sur une fonctionnalité', icon: Zap },
  { label: 'Question sur la facturation', icon: FileText },
  { label: 'Autre demande', icon: MessageSquare },
];

const INFO_CARDS = [
  {
    Icon: Clock,
    title: 'Temps de réponse',
    value: '< 24h',
    desc: 'En jours ouvrés',
    color: 'primary',
  },
  {
    Icon: Headphones,
    title: 'Support disponible',
    value: 'Lun – Ven',
    desc: '9h00 – 18h00',
    color: 'accent',
  },
  {
    Icon: MessageSquare,
    title: 'Canal de contact',
    value: 'Ticket',
    desc: 'Suivi garanti',
    color: 'success',
  },
];

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const SupportPage = () => {
  const [sujet, setSujet] = useState('');
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleQuickTopic = (label) => {
    setSujet(label);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsSubmitting(true);

    try {
      const response = await api.post('/support/tickets', { sujet, description });
      setMessage(response.data.message || 'Votre ticket a été créé avec succès !');
      setSujet('');
      setDescription('');
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sujetProgress = Math.round((sujet.length / 150) * 100);
  const descProgress = Math.round((description.length / 1000) * 100);

  return (
    <div className={styles.pageWrapper}>

      {/* ── Hero Header ── */}
      <div className={styles.hero}>
        <div className={styles.heroBadge}>
          <LifeBuoy size={14} strokeWidth={2.5} />
          Centre d'assistance
        </div>
        <h1 className={styles.heroTitle}>
          Comment pouvons-nous<br />
          <span className={styles.heroTitleAccent}>vous aider ?</span>
        </h1>
        <p className={styles.heroSubtitle}>
          Décrivez votre problème et notre équipe vous répondra rapidement.
        </p>
      </div>

      {/* ── Info Cards ── */}
      <div className={styles.infoGrid}>
        {INFO_CARDS.map(({ Icon, title, value, desc, color }) => (
          <div key={title} className={`${styles.infoCard} ${styles[`infoCard_${color}`]}`}>
            <div className={`${styles.infoIconWrap} ${styles[`infoIconWrap_${color}`]}`}>
              <Icon size={18} strokeWidth={2} />
            </div>
            <div className={styles.infoContent}>
              <p className={styles.infoTitle}>{title}</p>
              <p className={styles.infoValue}>{value}</p>
              <p className={styles.infoDesc}>{desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Carte principale ── */}
      <div className={styles.card}>

        {/* Alertes */}
        {message && (
          <div className={`${styles.alert} ${styles.alertSuccess}`} role="alert">
            <div className={styles.alertIconWrap}>
              <CheckCircle2 size={18} strokeWidth={2.5} />
            </div>
            <div className={styles.alertBody}>
              <p className={styles.alertTitle}>Ticket envoyé !</p>
              <p className={styles.alertMsg}>{message}</p>
            </div>
          </div>
        )}

        {error && (
          <div className={`${styles.alert} ${styles.alertError}`} role="alert">
            <div className={styles.alertIconWrap}>
              <AlertCircle size={18} strokeWidth={2.5} />
            </div>
            <div className={styles.alertBody}>
              <p className={styles.alertTitle}>Une erreur est survenue</p>
              <p className={styles.alertMsg}>{error}</p>
            </div>
          </div>
        )}

        {/* Sujets rapides */}
        <div className={styles.quickSection}>
          <p className={styles.quickLabel}>
            <Zap size={13} strokeWidth={2.5} />
            Sujets fréquents
          </p>
          <div className={styles.quickList}>
            {QUICK_TOPICS.map(({ label, icon: Icon }) => (
              <button
                key={label}
                type="button"
                className={`${styles.quickChip} ${sujet === label ? styles.quickChipActive : ''}`}
                onClick={() => handleQuickTopic(label)}
              >
                <Icon size={12} strokeWidth={2} />
                {label}
                <ChevronRight size={11} strokeWidth={2} className={styles.chipArrow} />
              </button>
            ))}
          </div>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className={styles.form} noValidate>

          {/* Sujet */}
          <div className={`${styles.formGroup} ${focusedField === 'sujet' ? styles.formGroupFocused : ''}`}>
            <div className={styles.labelRow}>
              <label htmlFor="sujet" className={styles.label}>
                <FileText size={14} strokeWidth={2} />
                Sujet du ticket
                <span className={styles.required} aria-hidden="true">*</span>
              </label>
              <span className={`${styles.charCount} ${sujet.length > 130 ? styles.charCountWarn : ''}`}>
                {sujet.length} / 150
              </span>
            </div>
            <div className={styles.inputWrap}>
              <input
                type="text"
                id="sujet"
                className={styles.input}
                placeholder="Ex : Problème lors de la génération du dossier"
                value={sujet}
                onChange={(e) => setSujet(e.target.value)}
                onFocus={() => setFocusedField('sujet')}
                onBlur={() => setFocusedField(null)}
                required
                disabled={isSubmitting}
                maxLength={150}
                autoComplete="off"
              />
              <div
                className={styles.progressBar}
                style={{ '--prog': `${sujetProgress}%` }}
                aria-hidden="true"
              />
            </div>
          </div>

          {/* Description */}
          <div className={`${styles.formGroup} ${focusedField === 'desc' ? styles.formGroupFocused : ''}`}>
            <div className={styles.labelRow}>
              <label htmlFor="description" className={styles.label}>
                <MessageSquare size={14} strokeWidth={2} />
                Description détaillée
                <span className={styles.required} aria-hidden="true">*</span>
              </label>
              <span className={`${styles.charCount} ${description.length > 900 ? styles.charCountWarn : ''}`}>
                {description.length} / 1000
              </span>
            </div>
            <div className={styles.inputWrap}>
              <textarea
                id="description"
                className={styles.textarea}
                rows={6}
                placeholder="Décrivez votre problème en détail : étapes pour le reproduire, message d'erreur affiché, comportement attendu…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onFocus={() => setFocusedField('desc')}
                onBlur={() => setFocusedField(null)}
                required
                disabled={isSubmitting}
                maxLength={1000}
              />
              <div
                className={styles.progressBar}
                style={{ '--prog': `${descProgress}%` }}
                aria-hidden="true"
              />
            </div>
            <p className={styles.fieldHint}>
              Plus votre description est précise, plus vite nous pourrons vous aider.
            </p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className={styles.submitBtn}
            disabled={isSubmitting || !sujet.trim() || !description.trim()}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} strokeWidth={2} className={styles.spinnerIcon} />
                Envoi en cours…
              </>
            ) : (
              <>
                <Send size={16} strokeWidth={2.5} />
                Envoyer le ticket
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SupportPage;